import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdateEconomic() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);
  const [calculatedAge, setCalculatedAge] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    dateOfBirth: '',
    isEmployed: '',
    occupation: '',
    selfEmploymentType: '',
    otherSelfEmploymentType: '',
    unemploymentReason: '',
    monthlyIncome: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getEconomicActivityDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message:
            response.message || 'Failed to load economic activity details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        dateOfBirth: response.record.dateOfBirth || '',
        isEmployed: response.record.isEmployed || '',
        occupation: response.record.occupation || '',
        selfEmploymentType: (() => {
          const val = response.record.selfEmploymentType || '';
          const options = response.legend?.selfEmploymentType || [];
          return val && !options.includes(val) ? 'Other' : val;
        })(),
        otherSelfEmploymentType: (() => {
          const val = response.record.selfEmploymentType || '';
          const options = response.legend?.selfEmploymentType || [];
          return val && !options.includes(val) ? val : (response.record.otherSelfEmploymentType || '');
        })(),
        unemploymentReason: response.record.unemploymentReason || '',
        monthlyIncome: response.record.monthlyIncome || '',
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));

      // Calculate age if DOB is present
      if (data.dateOfBirth) {
        calculateAge(data.dateOfBirth);
      }

      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) {
      setCalculatedAge(null);
      return;
    }

    const birthDate = new Date(dob);
    const today = new Date();

    if (isNaN(birthDate.getTime())) {
      setCalculatedAge(null);
      return;
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    setCalculatedAge(age);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updates = { [name]: value };

    // Handle date of birth change
    if (name === 'dateOfBirth') {
      calculateAge(value);
    }

    // Handle employment status change
    if (name === 'isEmployed') {
      if (value !== 'Yes') {
        updates.occupation = '';
        updates.selfEmploymentType = '';
        updates.otherSelfEmploymentType = '';
      }
      if (value !== 'No') {
        updates.unemploymentReason = '';
      }
    }

    // Handle occupation change
    if (name === 'occupation') {
      if (value !== 'Self-employment') {
        updates.selfEmploymentType = '';
        updates.otherSelfEmploymentType = '';
      }
    }

    // Handle self-employment type change
    if (name === 'selfEmploymentType' && value !== 'Other') {
      updates.otherSelfEmploymentType = '';
    }

    setFormData({
      ...formData,
      ...updates,
    });
  };

  const isOver16 = () => {
    return calculatedAge !== null && calculatedAge >= 16;
  };

  const shouldShowEmployedSection = () => {
    return isOver16() && formData.isEmployed === 'Yes';
  };

  const shouldShowUnemployedSection = () => {
    return isOver16() && formData.isEmployed === 'No';
  };

  const shouldShowSelfEmploymentSection = () => {
    return formData.occupation === 'Self-employment';
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.dateOfBirth) {
      setAlert({
        show: true,
        message: 'Please enter date of birth.',
        type: 'warning',
      });
      return;
    }

    if (isOver16()) {
      if (!formData.isEmployed) {
        setAlert({
          show: true,
          message: 'Please select employment status.',
          type: 'warning',
        });
        return;
      }

      if (formData.isEmployed === 'Yes' && !formData.occupation) {
        setAlert({
          show: true,
          message: 'Please select occupation type.',
          type: 'warning',
        });
        return;
      }

      if (formData.occupation === 'Self-employment') {
        const selfEmpType =
          formData.selfEmploymentType === 'Other'
            ? formData.otherSelfEmploymentType
            : formData.selfEmploymentType;

        if (!selfEmpType) {
          setAlert({
            show: true,
            message: 'Please select or specify self-employment type.',
            type: 'warning',
          });
          return;
        }
      }

      if (formData.isEmployed === 'No' && !formData.unemploymentReason) {
        setAlert({
          show: true,
          message: 'Please select reason for unemployment.',
          type: 'warning',
        });
        return;
      }
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      // Use custom self-employment type if "Other" is selected
      let finalSelfEmploymentType = formData.selfEmploymentType;
      if (
        formData.selfEmploymentType === 'Other' &&
        formData.otherSelfEmploymentType
      ) {
        finalSelfEmploymentType = formData.otherSelfEmploymentType;
      }

      const updateData = {
        uniqueId: recordData.uniqueId,
        dateOfBirth: formData.dateOfBirth,
        age: calculatedAge,
        isEmployed: isOver16() ? formData.isEmployed : '',
        occupation:
          isOver16() && formData.isEmployed === 'Yes'
            ? formData.occupation
            : '',
        selfEmploymentType:
          isOver16() &&
          formData.isEmployed === 'Yes' &&
          formData.occupation === 'Self-employment'
            ? finalSelfEmploymentType
            : '',
        unemploymentReason:
          isOver16() && formData.isEmployed === 'No'
            ? formData.unemploymentReason
            : '',
        monthlyIncome: isOver16() ? formData.monthlyIncome : '',
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateEconomicActivityDetails(
        updateData
      );

      if (response.success) {
        setAlert({
          show: true,
          message: 'Economic activity details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/assistance/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/vocational/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={8}
        title="Update Economic Activity Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading economic activity details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={8}
        title="Update Economic Activity Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load economic activity details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={8}
      title="Update Economic Activity Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">Date of Birth (Editable)</div>

        <Alert variant="info">
          <strong><i className="bi bi-calendar me-1"></i>Note:</strong> Economic activity questions are only
          applicable for persons over 16 years of age. Please verify and update
          the date of birth if incorrect.
        </Alert>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Date of Birth <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                required
                disabled={!canEdit}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Age Status</Form.Label>
              <div
                className={`age-badge ${
                  isOver16() ? 'eligible' : 'not-eligible'
                }`}
              >
                <strong>Age:</strong>{' '}
                {calculatedAge !== null ? `${calculatedAge} years` : '-'}
              </div>
            </Form.Group>
          </Col>
        </Row>

        {/* Economic Questions - Only show if over 16 */}
        {isOver16() && (
          <>
            <div className="section-header">Employment Status (Editable)</div>

            <Form.Group className="mb-3">
              <Form.Label>
                Do you work or doing business?{' '}
                <span className="text-danger">*</span>
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="isEmployed"
                  value="Yes"
                  checked={formData.isEmployed === 'Yes'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="isEmployed"
                  value="No"
                  checked={formData.isEmployed === 'No'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
              </div>
            </Form.Group>

            {/* Employed Section */}
            {shouldShowEmployedSection() && (
              <div className="conditional-field">
                <Form.Group className="mb-3">
                  <Form.Label>
                    Choose what kind of the occupation?{' '}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                  >
                    <option value="">Select...</option>
                    {legendData?.occupation?.map((item, index) => (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {shouldShowSelfEmploymentSection() && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>
                      What type of Self-employment?{' '}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <SelectWithOther
                      name="selfEmploymentType"
                      value={formData.selfEmploymentType}
                      otherName="otherSelfEmploymentType"
                      otherValue={formData.otherSelfEmploymentType}
                      options={legendData?.selfEmploymentType || []}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      otherPlaceholder="Please specify self-employment type"
                    />
                    <span className="info-badge">
                      For self-employed persons
                    </span>
                  </Form.Group>
                )}
              </div>
            )}

            {/* Unemployed Section */}
            {shouldShowUnemployedSection() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>
                  What is the main reason for non-availability of job /
                  self-employment opportunity?{' '}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="unemploymentReason"
                  value={formData.unemploymentReason}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                >
                  <option value="">Select...</option>
                  {legendData?.unemploymentReason?.map((item, index) => (
                    <option key={index} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
                <span className="info-badge">For unemployed persons</span>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Personal Monthly Income</Form.Label>
              <Form.Select
                name="monthlyIncome"
                value={formData.monthlyIncome}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {legendData?.monthlyIncome?.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </>
        )}

        {/* Under 16 Message */}
        {!isOver16() && calculatedAge !== null && (
          <Alert variant="warning">
            <strong><i className="bi bi-exclamation-triangle-fill me-1"></i>Person is under 16 years old</strong>
            <br />
            Economic activity questions are not applicable. Only Date of Birth
            can be updated.
          </Alert>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateEconomic;
